import { useState, useEffect, useCallback, useRef, useId } from "react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { apiGet, apiPost, apiDelete } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader, KpiRow, Card, SectionHeading, DataTable } from "../components/dc";
import { Button } from "../components/ui/button";
import { Upload, Loader2, Calculator, AlertTriangle } from "lucide-react";

const VIOLET = "#a855f7", CYAN = "#67e0f0", GREEN = "#34d399", RED = "#fb7185", AMBER = "#fbbf24", MUTE = "#9d8fc0";
const AXIS = { fontSize: 11, fill: MUTE };
const ttStyle = { background: "#17102b", border: "1px solid #2a1c47", borderRadius: 8, color: "#f5f2fb", fontSize: 12 };
const gbp0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
const gbp2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputStyle = { background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 8, padding: "7px 11px", fontSize: 14 };

// Type-to-search product picker (part code or description) for line-level modelling.
function ProductPicker({ value, options, onSelect, disabled }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const id = `product-picker-${useId().replace(/:/g, "")}`;
  const sel = options.find((o) => o.part === value);
  const display = open ? q : (sel ? `${sel.part} — ${sel.description}` : "");
  const ql = q.trim().toLowerCase();
  const matches = (!ql ? options : options.filter((o) =>
    (o.part || "").toLowerCase().includes(ql) || (o.description || "").toLowerCase().includes(ql))).slice(0, 40);
  return (
    <div style={{ position: "relative", minWidth: 320, flex: "1 1 320px", maxWidth: 420 }}>
      <input
        data-testid={id} role="combobox" aria-label="Product" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-options`} aria-activedescendant={open && active >= 0 ? `${id}-option-${active}` : undefined}
        value={display} disabled={disabled} placeholder="Search part number or description…"
        onFocus={() => { setOpen(true); setQ(""); setActive(-1); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }}
        onKeyDown={event => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault(); setOpen(true);
            setActive(index => event.key === "ArrowDown" ? Math.min(matches.length - 1, index + 1) : Math.max(0, index - 1));
          } else if (event.key === "Enter" && open && matches.length) {
            event.preventDefault(); onSelect(matches[Math.max(active, 0)].part); setOpen(false); setQ("");
          } else if (event.key === "Escape") { event.preventDefault(); setOpen(false); }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ ...inputStyle, width: "100%" }} />
      {open && matches.length > 0 && (
        <div id={`${id}-options`} role="listbox" data-testid={`${id}-options`} style={{ position: "absolute", zIndex: 30, top: "100%", left: 0, right: 0, marginTop: 4, maxHeight: 260, overflowY: "auto", background: "#17102b", border: "1px solid #2a1c47", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
          {matches.map((o, index) => (
            <div key={o.part} id={`${id}-option-${index}`} role="option" aria-selected={active === index} data-testid={`${id}-option-${index}`} onMouseDown={event => { event.preventDefault(); onSelect(o.part); setOpen(false); setQ(""); }}
              style={{ background: index === active ? "#342453" : undefined, padding: "7px 10px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid rgba(42,28,71,0.5)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontFamily: "monospace" }}>{o.part}</span> <span style={{ color: MUTE }}>— {o.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PriceModelling() {
  usePageTitle("QBR — Price Modelling");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState([{ supplier: "", pct: 4 }]); // one row per supplier
  const [products, setProducts] = useState([{ part: "", price: "" }]); // one row per product (exact new price)
  const [model, setModel] = useState(null);
  const [modelling, setModelling] = useState(false);
  const [mode, setMode] = useState("pct"); // "pct" | "file"
  const [forecastKind, setForecastKind] = useState(""); // "" run-rate | "operational" | "budgeted"
  const priceFileRef = useRef(null);
  const [scenarios, setScenarios] = useState([]);
  // Recent submitted price changes + their per-job impact. Kept entirely separate
  // from `model` so nothing here ever touches the headline figures.
  const [submissions, setSubmissions] = useState([]);
  const [subDetail, setSubDetail] = useState(null);
  const [subLoadingId, setSubLoadingId] = useState(null);

  const load = useCallback(async () => {
    const d = await apiGet("/qbr/cost-per-install");
    setData(d || { loaded: false });
    if (d?.suppliers?.length) setChanges((c) => (c[0]?.supplier ? c : [{ supplier: d.suppliers[0], pct: 4 }]));
  }, []);

  useEffect(() => { (async () => { setLoading(true); try { await load(); } finally { setLoading(false); } })(); }, [load]);


  const runModel = async () => {
    const rows = changes.filter((c) => c.supplier);
    if (!rows.length) return;
    setModelling(true);
    try {
      const m = await apiPost("/qbr/price-change-model", { changes: rows.map((c) => ({ supplier: c.supplier, pct: Number(c.pct) || 0 })), forecast: forecastKind });
      setModel(m);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not run the model.");
    } finally { setModelling(false); }
  };

  const runModelProducts = async () => {
    const rows = products.filter((p) => p.part && Number(p.price) > 0);
    if (!rows.length) return;
    setModelling(true);
    try {
      const m = await apiPost("/qbr/price-change-model", { products: rows.map((p) => ({ part: p.part, price: Number(p.price) })), forecast: forecastKind });
      setModel(m);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not model the products."); }
    finally { setModelling(false); }
  };

  const runModelFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setModelling(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (forecastKind) fd.append("forecast", forecastKind);
      const m = await apiPost("/qbr/price-change-model/file", fd, { timeout: 120000 });
      setModel(m);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not model the proposed price file.");
    } finally {
      setModelling(false);
      if (priceFileRef.current) priceFileRef.current.value = "";
    }
  };

  const loadScenarios = useCallback(async () => {
    const r = await apiGet("/qbr/scenarios");
    setScenarios(r?.scenarios || []);
  }, []);
  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  // Recent submitted price changes and their per-job impact (own state — never
  // feeds the headline).
  useEffect(() => {
    if (!data?.loaded) { setSubmissions([]); return; }
    (async () => {
      const r = await apiGet("/qbr/submission-impact");
      setSubmissions(r?.submissions || []);
    })();
  }, [data?.loaded, data?.uploadedAt]);

  const openSubDetail = async (id) => {
    if (subDetail?.submissionId === id) { setSubDetail(null); return; } // toggle closed
    setSubLoadingId(id);
    try {
      const d = await apiGet(`/qbr/submission-impact/${id}`);
      if (d) setSubDetail(d);
    } catch { toast.error("Could not load the submission detail."); }
    finally { setSubLoadingId(null); }
  };

  const saveScenario = async () => {
    if (!model) return;
    const suggested = `${model.supplier}${model.pct != null ? ` ${model.pct >= 0 ? "+" : ""}${model.pct}%` : ""}`;
    const name = window.prompt("Name this scenario:", suggested);
    if (!name) return;
    try {
      await apiPost("/qbr/scenarios", {
        name,
        kind: model.pct != null ? "pct" : "file",
        inputs: model.pct != null ? { supplier: model.supplier, pct: model.pct } : { label: model.supplier },
        overall: model.overall, byType: model.byType,
      });
      toast.success("Scenario saved.");
      loadScenarios();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save scenario."); }
  };

  const deleteScenario = async (id) => {
    try { await apiDelete(`/qbr/scenarios/${id}`); loadScenarios(); }
    catch { toast.error("Could not delete scenario."); }
  };

  if (loading) {
    return (<div data-testid="qbr-modelling"><PageHeader title="Price Modelling" subtitle="QBR" /><div style={{ color: MUTE, padding: 40 }}>Loading…</div></div>);
  }

  const loaded = !!data?.loaded;
  const types = data?.installTypes || [];
  const overall = data?.overall || {};
  const suppliers = data?.suppliers || [];
  const forecasts = data?.forecasts || {};

  return (
    <div data-testid="qbr-modelling">
      <PageHeader title="Price Modelling"
        subtitle={loaded && data.uploadedAt ? `BOM loaded ${new Date(data.uploadedAt).toLocaleDateString("en-GB")} · ${data.jobs?.toLocaleString()} jobs` : "Model how a supplier price change hits cost per install"} />

      {!loaded && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ color: MUTE, fontSize: 13.5, lineHeight: 1.6 }}>
            No data loaded yet. The metrics and tables below populate once the platform BOM is uploaded under Settings, Budget Modeller, Platform BOM (the line-level dispatch export, or a simple BOM). Recognised columns:
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {["primary_source_sku", "quantity_dispatched", "unit_cost_gbp", "lct_product", "supplier", "inferred_kf_job_ref", "product_description", "date_dispatched"].map((c) => (
                <span key={c} style={{ fontFamily: "monospace", fontSize: 12, color: "var(--color-text-2)", background: "#0f0a1e", border: "1px solid #2a1c47", borderRadius: 6, padding: "3px 8px" }}>{c}</span>
              ))}
            </div>
          </div>
        </Card>
      )}

      <KpiRow items={[
        { label: "Avg materials / job", value: loaded ? gbp0(overall.avgCost) : "—", valueColor: VIOLET,
          hint: "Average materials cost of one job, blended across all loaded jobs. Uses the initial dispatch only, costed at latest buying price (LBP)." },
        { label: "Annual material spend", value: loaded ? gbp0(data.annualSpend) : "—", valueColor: GREEN,
          hint: "Average materials per job × the annual run-rate of installs, i.e. the projected yearly materials spend at the current price." },
        { label: "Jobs analysed", value: loaded ? data.jobs?.toLocaleString() : "—", valueColor: "var(--color-text-1)",
          hint: "Distinct installs in the loaded data (resolved from the job reference, initial dispatch only)." },
        { label: "Install types", value: loaded ? types.length : "—", valueColor: CYAN,
          hint: "Job categories found in the data (Heat Pump, Solar, etc.) — the average is shown per type below." },
        { label: "Suppliers", value: loaded ? suppliers.length : "—", valueColor: "var(--color-text-1)",
          hint: "Distinct suppliers across the loaded jobs. Pick one below to model a price change." },
      ]} />

      <Card>
        <SectionHeading title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Calculator size={16} /> Model a price change</span>}
          subtitle="Apply a flat % change, or upload a proposed price file, and see the impact on the average job" />
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 2, background: "#0f0a1e", border: "1px solid var(--hairline)", borderRadius: 12, padding: 3 }}>
              {[["pct", "Flat %"], ["product", "By product"], ["file", "Proposed price file"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setMode(k)} disabled={!loaded} data-testid={`model-mode-${k}`}
                  style={{ border: "none", cursor: loaded ? "pointer" : "not-allowed", borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 600,
                    background: mode === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent", color: mode === k ? "#fff" : MUTE, opacity: loaded ? 1 : 0.5 }}>{lbl}</button>
              ))}
            </div>
            <div style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: MUTE }}>Volumes</span>
              <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 2, background: "#0f0a1e", border: "1px solid var(--hairline)", borderRadius: 12, padding: 3 }}>
                {[["", "Run-rate"], ["operational", "Operational"], ["budgeted", "Budgeted"]].map(([k, lbl]) => {
                  const avail = k === "" || forecasts[k];
                  return (
                    <button key={k || "runrate"} onClick={() => avail && setForecastKind(k)} disabled={!loaded || !avail} title={!avail ? "Set this forecast in Settings" : undefined} data-testid={`forecast-${k || "runrate"}`}
                      style={{ border: "none", cursor: loaded && avail ? "pointer" : "not-allowed", borderRadius: 999, padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
                        background: forecastKind === k ? "linear-gradient(90deg,#0ea5e9,#22d3ee)" : "transparent", color: forecastKind === k ? "#04121f" : MUTE, opacity: loaded && avail ? 1 : 0.4 }}>{lbl}</button>
                  );
                })}
              </div>
            </div>
          </div>
          {mode === "pct" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {changes.map((row, i) => (
                <div key={i} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, color: MUTE, width: 58 }}>{i === 0 ? "Supplier" : "and"}</span>
                  <select value={row.supplier} onChange={(e) => setChanges((cs) => cs.map((c, j) => j === i ? { ...c, supplier: e.target.value } : c))} style={inputStyle} disabled={!loaded} data-testid={`model-supplier-${i}`}>
                    {loaded ? suppliers.map((s) => <option key={s} value={s}>{s}</option>) : <option value="">—</option>}
                  </select>
                  <input type="number" step="0.5" value={row.pct} onChange={(e) => setChanges((cs) => cs.map((c, j) => j === i ? { ...c, pct: e.target.value } : c))} style={{ ...inputStyle, width: 84 }} disabled={!loaded} data-testid={`model-pct-${i}`} />
                  <span style={{ fontSize: 13, color: MUTE }}>%</span>
                  {changes.length > 1 && (
                    <button onClick={() => setChanges((cs) => cs.filter((_, j) => j !== i))} title="Remove" data-testid={`model-remove-${i}`}
                      style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: MUTE, cursor: "pointer", fontSize: 13, padding: "5px 10px", lineHeight: 1 }}>✕</button>
                  )}
                </div>
              ))}
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 2 }}>
                <button onClick={() => setChanges((cs) => [...cs, { supplier: suppliers[0] || "", pct: 4 }])} disabled={!loaded} data-testid="model-add-supplier"
                  style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: VIOLET, cursor: loaded ? "pointer" : "not-allowed", fontSize: 13, padding: "6px 12px" }}>+ Add supplier</button>
                <Button className="btn btn-primary" onClick={runModel} disabled={modelling || !loaded || !changes.some((c) => c.supplier)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="model-run">
                  {modelling ? <><Loader2 size={15} className="animate-spin" /> Modelling…</> : "Model impact"}
                </Button>
              </div>
            </div>
          ) : mode === "product" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {products.map((row, i) => {
                const cur = (overall.lines || []).find((l) => l.part === row.part);
                return (
                  <div key={i} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, color: MUTE, width: 58 }}>{i === 0 ? "Product" : "and"}</span>
                    <ProductPicker value={row.part} options={overall.lines || []} disabled={!loaded}
                      onSelect={(part) => setProducts((ps) => ps.map((p, j) => {
                        if (j !== i) return p;
                        const cur = (overall.lines || []).find((l) => l.part === part);
                        return { ...p, part, price: cur ? Number(cur.unitCost).toFixed(2) : p.price };
                      }))} />
                    <span style={{ fontSize: 12.5, color: MUTE, whiteSpace: "nowrap" }}>was {cur ? gbp2(cur.unitCost) : "—"} →</span>
                    <input type="number" step="0.01" min="0" placeholder="new £" value={row.price} onChange={(e) => setProducts((ps) => ps.map((p, j) => j === i ? { ...p, price: e.target.value } : p))} style={{ ...inputStyle, width: 100 }} disabled={!loaded} data-testid={`model-product-price-${i}`} />
                    {products.length > 1 && (
                      <button onClick={() => setProducts((ps) => ps.filter((_, j) => j !== i))} title="Remove"
                        style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: MUTE, cursor: "pointer", fontSize: 13, padding: "5px 10px", lineHeight: 1 }}>✕</button>
                    )}
                  </div>
                );
              })}
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 2 }}>
                <button onClick={() => setProducts((ps) => [...ps, { part: "", price: "" }])} disabled={!loaded} data-testid="model-add-product"
                  style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: VIOLET, cursor: loaded ? "pointer" : "not-allowed", fontSize: 13, padding: "6px 12px" }}>+ Add product</button>
                <Button className="btn btn-primary" onClick={runModelProducts} disabled={modelling || !loaded || !products.some((p) => p.part && Number(p.price) > 0)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="model-run-products">
                  {modelling ? <><Loader2 size={15} className="animate-spin" /> Modelling…</> : "Model impact"}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
              <input ref={priceFileRef} type="file" accept=".csv,text/csv" onChange={runModelFile} style={{ display: "none" }} data-testid="model-file-input" />
              <Button className="btn btn-primary" onClick={() => priceFileRef.current?.click()} disabled={modelling || !loaded}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="model-file-btn">
                {modelling ? <><Loader2 size={15} className="animate-spin" /> Modelling…</> : <><Upload size={15} /> Upload proposed prices</>}
              </Button>
              <span style={{ fontSize: 12, color: MUTE }}>CSV columns: <code>part_code</code>, <code>unit_cost</code>, optional <code>supplier</code>.</span>
            </div>
          )}
          {!loaded && <div style={{ fontSize: 12, color: MUTE, marginTop: 8 }}>Load the platform BOM in Settings to model changes.</div>}
        </div>

        {model && (
          <>
            <button onClick={() => setModel(null)} data-testid="model-back"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: VIOLET, cursor: "pointer", fontSize: 13, padding: "6px 12px", marginBottom: 14 }}>
              ← Back to overview
            </button>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", padding: "14px 16px", background: "#0f0a1e", border: "1px solid #2a1c47", borderRadius: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 14, color: MUTE }}>{model.supplier}{model.pct != null ? ` ${model.pct >= 0 ? "+" : ""}${model.pct}%` : ""} →</span>
              <span style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 700, color: model.overall.delta >= 0 ? RED : GREEN }}>
                {model.overall.delta >= 0 ? "+" : "−"}{gbp2(Math.abs(model.overall.delta))} / install
              </span>
              <span style={{ fontSize: 14, color: model.overall.delta >= 0 ? RED : GREEN }}>
                ({model.overall.delta >= 0 ? "+" : ""}{model.overall.deltaPct}% · {gbp0(model.overall.currentCost)} → {gbp0(model.overall.newCost)})
              </span>
              {model.overall.annualInstalls ? (
                <span style={{ fontSize: 14, color: MUTE }}>
                  ≈ {model.overall.annualDelta >= 0 ? "+" : "−"}{gbp0(Math.abs(model.overall.annualDelta))} / yr across ~{model.overall.annualInstalls.toLocaleString()} installs
                </span>
              ) : null}
            </div>
            <div style={{ marginBottom: 14 }}>
              <button onClick={saveScenario} data-testid="save-scenario"
                style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: VIOLET, cursor: "pointer", fontSize: 13, padding: "6px 14px" }}>+ Save scenario</button>
            </div>
            {(() => {
              const o = model.overall || {};
              const installs = o.annualInstalls || 0;
              // Plot the cumulative EXTRA cost the change adds (proposed − current).
              // Two total-spend lines overlap when the % change is small; the delta
              // auto-scales to the impact and a £0 start is meaningful (nothing
              // accrued yet). Uses the forecast ramp when set, else a run-rate line.
              let series;
              if (model.projection?.length) {
                series = model.projection.map((p) => ({ month: p.month, extra: Math.round(p.proposed - p.current) }));
              } else {
                const annual = o.annualDelta != null ? o.annualDelta : (o.delta || 0) * installs;
                if (!installs && !annual) return null;
                series = Array.from({ length: 13 }, (_, m) => ({ month: m === 0 ? "Now" : `M${m}`, extra: Math.round((annual / 12) * m) }));
              }
              const up = (o.delta || 0) >= 0;
              const col = up ? RED : GREEN;
              const fmt = (v) => (Math.abs(v) >= 1e6 ? `£${(v / 1e6).toFixed(2)}m` : `£${Math.round(v / 1000)}k`);
              const final = series[series.length - 1].extra;
              const basis = model.forecast ? `${model.forecast} forecast` : "run-rate (no forecast set)";
              return (
                <div style={{ marginBottom: 18 }}>
                  <SectionHeading title={up ? "Extra cost from this change, building over 12 months" : "Saving from this change, building over 12 months"}
                    subtitle={up
                      ? `Cumulative added spend vs today's prices — +${gbp0(Math.abs(final))} by month 12 · ${basis}`
                      : `Cumulative saving vs today's prices — ${gbp0(Math.abs(final))} by month 12 · ${basis}`} />
                  <div style={{ height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={series} margin={{ top: 6, right: 12, left: 10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="extraFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={col} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={col} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#241a3d" vertical={false} />
                        <XAxis dataKey="month" tick={AXIS} />
                        <YAxis tick={AXIS} tickFormatter={fmt} width={62} />
                        <Tooltip contentStyle={ttStyle} formatter={(v) => [gbp0(v), "Extra cost"]} />
                        <Area type="monotone" dataKey="extra" stroke={col} strokeWidth={2.5} fill="url(#extraFill)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <SectionHeading title="Impact by install type" subtitle="Only the job types these prices affect" />
                <DataTable perPage={8}
                  columns={[
                    { key: "type", label: "Type", width: "1.4fr" },
                    { key: "currentCost", label: "Now", align: "right", width: "1fr", render: (r) => gbp2(r.currentCost), hint: "Average materials per job for this type at today's prices." },
                    { key: "newCost", label: "New", align: "right", width: "1fr", render: (r) => gbp2(r.newCost), hint: "Average materials per job for this type after the modelled change." },
                    { key: "delta", label: "Δ", align: "right", width: "0.9fr", render: (r) => <span style={{ color: r.delta >= 0 ? RED : GREEN, fontWeight: 600 }}>{r.delta >= 0 ? "+" : "−"}{gbp2(Math.abs(r.delta))}</span>, hint: "Change in the average job for this type (new minus now)." },
                  ]}
                  rows={(model.byType || []).filter((t) => Math.abs(t.delta || 0) > 0.005)} />
              </div>
              <div>
                <SectionHeading title="What drives it" subtitle="Lines moved by the change, by impact per install" />
                <DataTable perPage={8}
                  columns={[
                    { key: "part", label: "Part", width: "0.9fr" },
                    { key: "supplier", label: "Supplier", width: "1fr" },
                    { key: "changePct", label: "Change", align: "right", width: "0.7fr", render: (r) => r.changePct != null ? `${r.changePct >= 0 ? "+" : ""}${r.changePct}%` : "—", hint: "The % applied to this line's supplier." },
                    { key: "impactPerInstall", label: "£/inst", align: "right", width: "0.9fr", render: (r) => <span style={{ color: AMBER, fontWeight: 600 }}>{gbp2(r.impactPerInstall)}</span>, hint: "How much this part alone shifts the average job under the change." },
                  ]}
                  rows={model.drivers} />
              </div>
            </div>
            {(() => {
              const affected = (model.byVariant || []).filter((v) => Math.abs(v.delta || 0) > 0.005);
              if (!affected.length) return null;
              return (
                <div style={{ marginTop: 16 }}>
                  <SectionHeading title="Impact by install variant" subtitle="Only the appliance variants these prices actually affect" />
                  <DataTable perPage={8}
                    columns={[
                      { key: "variant", label: "Variant", width: "1.2fr" },
                      { key: "area", label: "Area", width: "0.9fr" },
                      { key: "jobCount", label: "Jobs", align: "right", width: "0.7fr" },
                      { key: "currentCost", label: "Now", align: "right", width: "1fr", render: (r) => gbp2(r.currentCost) },
                      { key: "newCost", label: "New", align: "right", width: "1fr", render: (r) => gbp2(r.newCost) },
                      { key: "delta", label: "Δ / job", align: "right", width: "1fr", render: (r) => <span style={{ color: r.delta >= 0 ? RED : GREEN, fontWeight: 600 }}>{r.delta >= 0 ? "+" : "−"}{gbp2(Math.abs(r.delta))} ({r.deltaPct >= 0 ? "+" : ""}{r.deltaPct}%)</span> },
                    ]}
                    rows={affected} />
                </div>
              );
            })()}
            {model.uomWarnings?.length > 0 && (
              <div data-testid="uom-warnings" style={{ marginTop: 16, padding: "12px 14px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <AlertTriangle size={15} style={{ color: "#f59e0b" }} />
                  <span style={{ fontWeight: 700, color: "#f59e0b" }}>Possible unit-of-measure mismatch — excluded from the impact above</span>
                </div>
                <div style={{ fontSize: 13, color: "#9a7b3a", marginBottom: 8 }}>
                  These prices sit on a clean multiple of what we dispatch, so they look like a different unit (a pack, coil, or 3m length) rather than a real price move. Check the unit before trusting the number.
                </div>
                <DataTable perPage={6}
                  columns={[
                    { key: "part", label: "Part", width: "1fr" },
                    { key: "description", label: "Description", width: "1.6fr" },
                    { key: "dispatchUnit", label: "Dispatched", align: "right", width: "1fr", render: (r) => gbp2(r.dispatchUnit) },
                    { key: "submitted", label: "Submitted", align: "right", width: "1fr", render: (r) => gbp2(r.submitted) },
                    { key: "factor", label: "Looks like", align: "right", width: "0.8fr", render: (r) => <span style={{ color: AMBER, fontWeight: 600 }}>~{r.factor}×</span> },
                  ]}
                  rows={model.uomWarnings} />
              </div>
            )}
          </>
        )}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        <Card>
          <SectionHeading title="Average materials per job — by type" subtitle="Initial dispatch only · costed at latest buying price (LBP)" />
          <DataTable perPage={8}
            columns={[
              { key: "type", label: "Install type", width: "1.6fr" },
              { key: "jobCount", label: "Jobs", align: "right", width: "0.7fr", hint: "Distinct jobs of this type in the data." },
              { key: "avgCost", label: "Avg materials / job", align: "right", width: "1.1fr", render: (r) => gbp2(r.avgCost), hint: "Sum of every part's average per-job cost for this install type." },
            ]}
            rows={types} />
        </Card>
        <Card>
          <SectionHeading title="Cost composition" subtitle="Top parts by cost per job (blended)" />
          <DataTable perPage={8} searchKeys={["part", "supplier", "description"]} searchPlaceholder="Search part / supplier…"
            columns={[
              { key: "part", label: "Part", width: "1fr", hint: "The part / SKU code from the dispatch data." },
              { key: "supplier", label: "Supplier", width: "1fr" },
              { key: "avgQty", label: "Qty/inst", align: "right", width: "0.7fr", hint: "Average quantity of this part used on one job." },
              { key: "unitCost", label: "Unit", align: "right", width: "0.8fr", render: (r) => gbp2(r.unitCost), hint: "Cost of one unit, at latest buying price (LBP)." },
              { key: "costPerInstall", label: "£/job", align: "right", width: "0.9fr", render: (r) => gbp2(r.costPerInstall), hint: "Qty per job × unit cost — this part's share of the average job." },
            ]}
            rows={overall.lines || []} />
        </Card>
      </div>

      {loaded && (data.variants || []).length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <SectionHeading title="Average materials per job — by install variant" subtitle="Appliance brand per job (Cosy / Daikin / Grant, Fox / Enphase / Tesla). Manage the rules in Settings → Variant Map." />
          <DataTable perPage={10}
            columns={[
              { key: "variant", label: "Variant", width: "1.2fr" },
              { key: "area", label: "Area", width: "1fr" },
              { key: "jobCount", label: "Jobs", align: "right", width: "0.7fr" },
              { key: "avgCost", label: "Avg materials / job", align: "right", width: "1.1fr", render: (r) => gbp2(r.avgCost) },
            ]}
            rows={data.variants} />
        </Card>
      )}

      {loaded && scenarios.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <SectionHeading title="Saved scenarios" subtitle="Compare what-ifs side by side · cost per install and per year" />
          <DataTable perPage={8}
            columns={[
              { key: "name", label: "Scenario", width: "1.8fr" },
              { key: "kind", label: "Type", width: "0.8fr", render: (r) => r.kind === "file" ? "Price file" : "Flat %" },
              { key: "delta", label: "Δ / install", align: "right", width: "1fr", render: (r) => <span style={{ color: (r.overall?.delta || 0) >= 0 ? RED : GREEN, fontWeight: 600 }}>{(r.overall?.delta || 0) >= 0 ? "+" : "−"}{gbp2(Math.abs(r.overall?.delta || 0))}</span> },
              { key: "deltaPct", label: "%", align: "right", width: "0.7fr", render: (r) => `${(r.overall?.deltaPct || 0) >= 0 ? "+" : ""}${r.overall?.deltaPct || 0}%` },
              { key: "annualDelta", label: "Δ / yr", align: "right", width: "1fr", render: (r) => r.overall?.annualDelta != null ? <span style={{ color: (r.overall.annualDelta || 0) >= 0 ? RED : GREEN }}>{(r.overall.annualDelta || 0) >= 0 ? "+" : "−"}{gbp0(Math.abs(r.overall.annualDelta))}</span> : "—" },
              { key: "del", label: "", align: "right", width: "0.6fr", render: (r) => <button onClick={() => deleteScenario(r.id)} style={{ background: "none", border: "none", color: MUTE, cursor: "pointer", fontSize: 14 }}>✕</button> },
            ]}
            rows={scenarios} />
        </Card>
      )}

      {loaded && submissions.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <SectionHeading title="Recent price changes — impact per job"
            subtitle="Submitted price files run through the model. Reference only — these do not affect the figures above. Click a row for line-level detail." />
          <DataTable perPage={8} searchKeys={["supplier"]} searchPlaceholder="Search supplier…"
            columns={[
              { key: "supplier", label: "Supplier", width: "1.5fr" },
              { key: "submittedAt", label: "Submitted", align: "right", width: "1fr", render: (r) => r.submittedAt ? new Date(r.submittedAt).toLocaleDateString("en-GB") : "—" },
              { key: "matchedCount", label: "Lines", align: "right", width: "0.7fr", render: (r) => `${r.matchedCount}/${r.priceCount}`, hint: "Submitted lines that affect installs / total submitted." },
              { key: "delta", label: "Δ / job", align: "right", width: "1.1fr", render: (r) => <span style={{ color: r.overall.delta >= 0 ? RED : GREEN, fontWeight: 600 }}>{r.overall.delta >= 0 ? "+" : "−"}{gbp2(Math.abs(r.overall.delta))} ({r.overall.deltaPct >= 0 ? "+" : ""}{r.overall.deltaPct}%)</span> },
              { key: "view", label: "", align: "right", width: "0.7fr", render: (r) => <button onClick={() => openSubDetail(r.id)} style={{ background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: VIOLET, cursor: "pointer", fontSize: 12, padding: "4px 10px" }}>{subLoadingId === r.id ? "…" : (subDetail?.submissionId === r.id ? "Hide" : "View")}</button> },
            ]}
            rows={submissions} />

          {subDetail && (
            <div style={{ marginTop: 16, padding: "14px 16px", background: "#0f0a1e", border: "1px solid #2a1c47", borderRadius: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
                <span style={{ fontWeight: 600, color: "var(--color-text-1)" }}>{subDetail.supplier}</span>
                <span style={{ fontSize: 13, color: MUTE }}>
                  {subDetail.overall.delta >= 0 ? "+" : "−"}{gbp2(Math.abs(subDetail.overall.delta))} / job ({subDetail.overall.deltaPct >= 0 ? "+" : ""}{subDetail.overall.deltaPct}% · {gbp0(subDetail.overall.currentCost)} → {gbp0(subDetail.overall.newCost)})
                </span>
              </div>
              {(() => {
                const affected = (subDetail.byType || []).filter((t) => Math.abs(t.delta || 0) > 0.005);
                if (!affected.length) return null;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <SectionHeading title="Impact by job type" subtitle="Only the job types these prices actually affect" />
                    <DataTable perPage={8}
                      columns={[
                        { key: "type", label: "Job type", width: "1.4fr" },
                        { key: "jobCount", label: "Jobs", align: "right", width: "0.7fr" },
                        { key: "currentCost", label: "Now", align: "right", width: "1fr", render: (r) => gbp2(r.currentCost) },
                        { key: "newCost", label: "New", align: "right", width: "1fr", render: (r) => gbp2(r.newCost) },
                        { key: "delta", label: "Δ / job", align: "right", width: "1fr", render: (r) => <span style={{ color: r.delta >= 0 ? RED : GREEN, fontWeight: 600 }}>{r.delta >= 0 ? "+" : "−"}{gbp2(Math.abs(r.delta))} ({r.deltaPct >= 0 ? "+" : ""}{r.deltaPct}%)</span> },
                      ]}
                      rows={affected} />
                  </div>
                );
              })()}
              <SectionHeading title="Line-level impact" subtitle="How each submitted price moves the average job" />
              <DataTable perPage={10}
                columns={[
                  { key: "part", label: "Part", width: "0.9fr" },
                  { key: "description", label: "Description", width: "1.6fr" },
                  { key: "unitCost", label: "Was", align: "right", width: "0.8fr", render: (r) => gbp2(r.unitCost) },
                  { key: "newUnit", label: "New", align: "right", width: "0.8fr", render: (r) => r.newUnit != null ? gbp2(r.newUnit) : "—" },
                  { key: "avgQty", label: "Qty/job", align: "right", width: "0.7fr" },
                  { key: "impactPerInstall", label: "Δ / job", align: "right", width: "0.9fr", render: (r) => <span style={{ color: (r.impactPerInstall || 0) >= 0 ? RED : GREEN, fontWeight: 600 }}>{(r.impactPerInstall || 0) >= 0 ? "+" : "−"}{gbp2(Math.abs(r.impactPerInstall || 0))}</span> },
                ]}
                rows={subDetail.drivers || []} />
              {subDetail.uomWarnings?.length > 0 && (
                <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, fontSize: 12.5, color: "#c99a3c" }}>
                  <AlertTriangle size={13} style={{ color: "#f59e0b", verticalAlign: "middle", marginRight: 6 }} />
                  {subDetail.uomWarnings.length} line(s) excluded as a likely unit mismatch (e.g. {subDetail.uomWarnings[0].part} at ~{subDetail.uomWarnings[0].factor}×).
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
